import styled from 'styled-components'
import React from 'react'

export const Container = styled.div`
    height: 100%;
    min-width: 100px;
    display: flex;
    flex-direction: column;
`

const Title = styled.span`
    font-family: SourceSansPro-Semibold;
    padding: ${props => (props.small ? '6px 12px' : '8px 16px')};
    font-size: 14px;
    font-weight: 600;
    text-transform: uppercase;
    border-top: solid 1px #dedede;
    border-bottom: solid 1px #dedede;
    background-color: #f1f1f1;
`

export const Horizontal = styled.div`
    height: 100%;
    display: flex;
`

export const Chart = styled.div`
    height: 100%;
    width: ${({ widthPct, leftWidthPct, fullWidth, minWidth, maxWidth }) =>
        widthPct === undefined
            ? '50%'
            : minWidth && maxWidth
            ? `calc(100% - ${
                  leftWidthPct === 100 ? '100%' : Math.min((leftWidthPct / 100) * fullWidth, maxWidth) + 'px'
              })`
            : widthPct + '%'};
    max-width: ${({ maxWidth, widthPct, minWidth }) =>
        maxWidth && !minWidth && widthPct < 100 ? `${maxWidth}px` : 'initial'};
    min-width: ${({ minWidth, widthPct }) => (minWidth ? `${widthPct}%` : 'initial')};

    padding-top: 32px;

    &:not(:last-child) {
        border-right: solid 1px #dedede;
    }

    ${({ value, suffixBottom }) =>
        value &&
        `
        .value-widget {
            font-size: 0vh;
            line-height: 0vh;
            height: auto !important;
            top: 50% !important;
            transform: translateY(-25%);

            #value-widget-content {
                display: ${suffixBottom ? 'block' : 'flex'};
                justify-content: center;

                span {
                    display: ${suffixBottom ? 'block' : 'inline'};
                    text-align: center;

                    &.value {
                        height: 3vh;
                        font-size: 3vh;
                        line-height: 3vh;
                    }

                    &.prefix,
                    &.suffix {
                        height: 2vh;
                        font-size: 2vh;
                        line-height: ${(suffixBottom ? 2 : 3.5) + 'vh'};
                    }
                }

                #ref {
                    display: none !important;
                }
            }
        }
    `}

    // TODO: Fix plotline flags being rendered outside chart container
    .highcharts-container + .plotline-flag ,
    .plotline-flag + .plotline-flag {
        display: none !important;
    }

    [data-highcharts-chart] {
        overflow: visible !important;
    }
`

const ConfigContainer = styled.div`
    border: 1px solid #ccc;
    padding: 6px;
    border-radius: 4px;
    margin-bottom: 16px;

    .config-container-title {
        font-size: 15px;
        font-family: SourceSansPro-Semibold;
    }
`

export const FormGroup = ({ labelText, style, children }) => (
    <div className="form-group" style={style}>
        <label>{labelText}</label>
        {children}
    </div>
)

const Radio = styled.div`
    display: flex;
    margin-top: ${props => props.marginTop || '0px'};

    > * {
        padding-right: ${props => props.paddingRight || '4px'};
    }

    div:not(:last-child) {
        margin-right: 24px;
    }
`

const RadioItem = styled.div`
    display: flex;
    align-items: center;

    label {
        padding-bottom: 0;
    }

    input {
        margin-right: 4px;
    }
`

export const ValueConfiguration = ({
    prefix,
    setPrefix,
    suffix,
    setSuffix,
    suffixBottom,
    setSuffixBottom,
    fractionDigits,
    setFractionDigits,
    onChange
}) => (
    <ConfigContainer>
        <span className="config-container-title">Value configuration</span>

        <FormGroup labelText="Prefix">
            <input type="text" value={prefix} onChange={e => setPrefix(e.target.value)} />
        </FormGroup>

        <FormGroup labelText="Suffix" style={{ marginTop: 12 }}>
            <input type="text" value={suffix} onChange={e => setSuffix(e.target.value)} />
        </FormGroup>
        <FormGroup labelText="Suffix Position">
            <Radio paddingRight="16">
                <RadioItem style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="radio" name="bottom" checked={suffixBottom} onChange={() => setSuffixBottom(true)} />
                    <label htmlFor="bottom">Bottom</label>
                </RadioItem>
                <RadioItem style={{ display: 'flex', alignItems: 'center' }}>
                    <input type="radio" name="right" checked={!suffixBottom} onChange={() => setSuffixBottom(false)} />
                    <label htmlFor="right">Right</label>
                </RadioItem>
            </Radio>
        </FormGroup>

        <FormGroup labelText="Fraction digits">
            <input
                type="number"
                min="0"
                value={fractionDigits}
                onChange={e => setFractionDigits(parseInt(e.target.value))}
            />
        </FormGroup>

        <button
            className="btn value-config-apply"
            onClick={() => {
                onChange('suffix', suffix)
                onChange('prefix', prefix)
                onChange('suffixBottom', suffixBottom)
                onChange('fractionDigits', fractionDigits)
            }}
        >
            Apply
        </button>
    </ConfigContainer>
)

const onChangeWidth = (chart, leftChart, rightChart, setLeftWidth, setRightWidth, { target: { value: v } }) => {
    const value = parseInt(v)
    if (typeof value === 'number' && value >= 0 && value <= 100) {
        if (chart === leftChart) {
            setLeftWidth(value)
            setRightWidth(100 - value)
        } else if (chart === rightChart) {
            setRightWidth(value)
            setLeftWidth(100 - value)
        }
    }
}

export const WidthConfiguration = ({
    leftWidth,
    setLeftWidth,
    leftMaxWidth,
    setLeftMaxWidth,
    leftChart,
    rightWidth,
    setRightWidth,
    rightChart,
    onChange
}) => (
    <ConfigContainer>
        <span className="config-container-title">Width configuration</span>

        <FormGroup labelText={`${leftChart[0].toUpperCase() + leftChart.slice(1)} (%)`}>
            <input
                type="number"
                min="0"
                max="100"
                value={leftWidth}
                onChange={e => onChangeWidth(leftChart, leftChart, rightChart, setLeftWidth, setRightWidth, e)}
            />
        </FormGroup>

        <FormGroup labelText={`${rightChart[0].toUpperCase() + rightChart.slice(1)} (%)`}>
            <input
                type="number"
                min="0"
                max="100"
                value={rightWidth}
                onChange={e => onChangeWidth(rightChart, leftChart, rightChart, setLeftWidth, setRightWidth, e)}
            />
        </FormGroup>

        <FormGroup labelText={`${leftChart[0].toUpperCase() + leftChart.slice(1)} max width (px)`}>
            <input type="number" min="0" value={leftMaxWidth} onChange={e => setLeftMaxWidth(e.target.value)} />
        </FormGroup>

        <button
            className="btn width-config-apply"
            onClick={() => {
                onChange(`${leftChart}Width`, leftWidth)
                onChange(`${rightChart}Width`, rightWidth)
                onChange(`${leftChart}MaxWidth`, leftMaxWidth)
            }}
        >
            Apply
        </button>
    </ConfigContainer>
)
